import { AfterViewInit, Component, Input } from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { TranslationService } from '@mirage-projects/cw-commons';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';

/**
 * This component is the summary step for the import wizards.
 * It shows the summary for the options selected
 */
@Component({
    selector: 'cw-create-wizard-summary',
    templateUrl: './create-wizard-summary.component.html',
    styleUrls: ['./create-wizard-summary.component.scss'],
    standalone: false,
})
export class CreateWizardSummaryComponent implements AfterViewInit {
    /** Text that will be shown as a definition of the wizard in all the steps */
    @Input() subtitle = '';
    /** When the credits estimation can't be calculated, a warning message should appear */
    @Input() showEstimationMessage = false;

    private onDestroy$ = new Subject<boolean>();
    private stepChanged$ = this.uploadWizardService.stepChanged.pipe(takeUntil(this.onDestroy$));
    private wizardTabName = 'WIZARD.TAB_SUMMARY';

    constructor(
        public uploadWizardService: UploadWizardService,
        private translationService: TranslationService
    ) {}

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.listenToStepChanged();
        });
    }

    /**
     * Shows/hides the button depending on whether is a job site, the importType and whether the uploading has been started
     * @returns true if the wizard isn't opened within a job site, the importType is project or layer and the upload has started, false otherwise
     */
    showUploadMoreData(): boolean {
        return this.uploadWizardService.startedUpload && !this.uploadWizardService.jobSiteId && this.isNewImportWizardType();
    }

    /**
     * Shows/hides the warning message depending on the importType, whether is a job site and whether the uploading has been started
     * @returns true if the importType is Project, the job site doesn't exist and the upload has started
     */
    showImportZipProjectWarning(): boolean {
        return this.uploadWizardService.isProject() && this.showUploadMoreData();
    }

    /**
     * Shows/hides the upload control buttons depending on the importType and on whether there is a job site
     * @returns false if the component is opened within a site and the importType is reference data, true otherwise
     *
     */
    showUploadControlButtons(): boolean {
        return !(this.uploadWizardService.jobSiteId && this.uploadWizardService.isImportJobSiteReferenceData());
    }

    private listenToStepChanged(): void {
        this.stepChanged$
            .pipe(
                takeUntil(this.onDestroy$),
                filter((item: any) => {
                    return item?.wizardSteps?.selectedItem.label === this.translationService.translate(this.wizardTabName);
                })
            )
            .subscribe(() => {
                this.configureSummaryTitleAndNotice();
            });
    }

    private configureSummaryTitleAndNotice(): void {
        if (this.uploadWizardService.isProject() || !this.uploadWizardService.isLayer() || !!this.uploadWizardService.jobSiteId) {
            return;
        }

        const summaryPosition = 50;

        this.uploadWizardService.summary[summaryPosition] = {
            title: 'WIZARD.CONVERT_TITLE',
            notice: this.uploadWizardService.layerNoticeSummary,
            fields: [],
        };
    }

    private isNewImportWizardType(): boolean {
        return this.uploadWizardService.isProject() || this.uploadWizardService.isLayer();
    }
}
